import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, IconButton, Tooltip, Chip, Collapse, InputAdornment, Switch } from '@mui/material';
import { Search as SearchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import { METHOD_HINTS, methodLabel, scopeOf, writeMethodsOf, describeDomainSelection, groupScopes } from '../utils/apiScopeUtils';

const PickerRoot = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const Toolbar = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const SearchField = styled(TextField)(({ theme }) => ({
    flex: '1 1 220px',
    minWidth: 180,
}));

const ToolbarSpacer = styled(Box)({
    flex: '1 1 auto',
});

const CountText = styled(Typography, { shouldForwardProp: (prop) => prop !== 'over' })(({ theme, over }) => ({
    color: over ? theme.palette.error.main : theme.palette.text.secondary,
    whiteSpace: 'nowrap',
}));

const ScrollArea = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 340,
    overflowY: 'auto',
}));

const DomainRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-of-type': {
        borderBottom: 'none',
    },
}));

const DomainRowTop = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const DomainNameBlock = styled(Box)({
    flex: '1 1 180px',
    minWidth: 0,
});

const DomainToggles = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const MethodGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.5),
}));

const EmptyRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const SummaryBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
}));

const ApiKeyScopePicker = ({ domains = [], value = [], onChange, maxScopes = 0, disabled = false }) => {
    const [search, setSearch] = useState('');
    const [selectedOnly, setSelectedOnly] = useState(false);
    const [expanded, setExpanded] = useState({});

    const selected = useMemo(() => new Set(value || []), [value]);

    const selectedMethodsFor = (entry) => (entry.methods || []).filter((m) => selected.has(scopeOf(entry.domain, m)));

    const applyChange = (next) => {
        if (typeof onChange === 'function') onChange([...new Set(next)]);
    };

    const setScopes = (scopes, on) => {
        const next = new Set(selected);
        scopes.forEach((scope) => (on ? next.add(scope) : next.delete(scope)));
        applyChange([...next]);
    };

    const visibleDomains = useMemo(() => {
        const term = search.trim().toLowerCase();
        return (domains || []).filter((entry) => {
            if (term && !`${entry.label} ${entry.domain}`.toLowerCase().includes(term)) return false;
            if (selectedOnly && !(entry.methods || []).some((m) => selected.has(scopeOf(entry.domain, m)))) return false;
            return true;
        });
    }, [domains, search, selectedOnly, selected]);

    const selectAllRead = () => setScopes((domains || []).map((entry) => scopeOf(entry.domain, 'GET')), true);

    const grouped = useMemo(() => groupScopes([...selected], domains), [selected, domains]);
    const overLimit = maxScopes > 0 && selected.size > maxScopes;

    return (
        <PickerRoot>
            <Toolbar>
                <SearchField
                    size="small"
                    placeholder="Find an area, e.g. notifications"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={disabled}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControlLabel
                    control={<Switch size="small" checked={selectedOnly} onChange={(e) => setSelectedOnly(e.target.checked)} disabled={disabled} />}
                    label={<Typography variant="body2">Only chosen</Typography>}
                />
                <Tooltip title="Tick View on every area, so the key can read but never change anything">
                    <span>
                        <Button size="small" onClick={selectAllRead} disabled={disabled}>
                            View everything
                        </Button>
                    </span>
                </Tooltip>
                <Button size="small" color="inherit" onClick={() => applyChange([])} disabled={disabled || selected.size === 0}>
                    Clear
                </Button>
                <ToolbarSpacer />
                <CountText variant="body2" over={overLimit ? 1 : 0}>
                    {selected.size} chosen{maxScopes ? ` / ${maxScopes} max` : ''}
                </CountText>
            </Toolbar>

            <ScrollArea>
                {visibleDomains.length === 0 ? (
                    <EmptyRow>
                        <Typography variant="body2" color="text.secondary">
                            {selectedOnly ? 'Nothing chosen yet.' : 'No area matches that search.'}
                        </Typography>
                    </EmptyRow>
                ) : (
                    visibleDomains.map((entry) => {
                        const writeMethods = writeMethodsOf(entry);
                        const chosen = selectedMethodsFor(entry);
                        const chosenWrites = chosen.filter((m) => m !== 'GET');
                        const readOnlyArea = writeMethods.length === 0;
                        const isExpanded = !!expanded[entry.domain];

                        return (
                            <DomainRow key={entry.domain}>
                                <DomainRowTop>
                                    <DomainNameBlock>
                                        <Typography variant="body2" fontWeight="bold">
                                            {entry.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {describeDomainSelection(entry, chosen)}
                                            {readOnlyArea ? ' — read-only area' : ''}
                                        </Typography>
                                    </DomainNameBlock>
                                    <DomainToggles>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selected.has(scopeOf(entry.domain, 'GET'))}
                                                    onChange={(e) => setScopes([scopeOf(entry.domain, 'GET')], e.target.checked)}
                                                    disabled={disabled}
                                                />
                                            }
                                            label={<Typography variant="body2">View</Typography>}
                                        />
                                        <Tooltip title={readOnlyArea ? 'API keys can only read this area' : 'Allow the key to create, update and delete here'}>
                                            <span>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={writeMethods.length > 0 && chosenWrites.length === writeMethods.length}
                                                            indeterminate={chosenWrites.length > 0 && chosenWrites.length < writeMethods.length}
                                                            onChange={(e) => setScopes(writeMethods.map((m) => scopeOf(entry.domain, m)), e.target.checked)}
                                                            disabled={disabled || readOnlyArea}
                                                        />
                                                    }
                                                    label={<Typography variant="body2">Change</Typography>}
                                                />
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={isExpanded ? 'Hide individual actions' : 'Choose individual actions'}>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setExpanded((prev) => ({ ...prev, [entry.domain]: !prev[entry.domain] }))}
                                                    disabled={disabled || readOnlyArea}
                                                >
                                                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </DomainToggles>
                                </DomainRowTop>
                                <Collapse in={isExpanded && !readOnlyArea} unmountOnExit>
                                    <MethodGrid>
                                        {(entry.methods || []).map((method) => (
                                            <Tooltip key={method} title={METHOD_HINTS[method] || method}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={selected.has(scopeOf(entry.domain, method))}
                                                            onChange={(e) => setScopes([scopeOf(entry.domain, method)], e.target.checked)}
                                                            disabled={disabled}
                                                        />
                                                    }
                                                    label={<Typography variant="caption">{methodLabel(method)}</Typography>}
                                                />
                                            </Tooltip>
                                        ))}
                                    </MethodGrid>
                                </Collapse>
                            </DomainRow>
                        );
                    })
                )}
            </ScrollArea>

            {grouped.length > 0 && (
                <SummaryBox>
                    {grouped.map((group) => (
                        <Chip
                            key={group.domain}
                            size="small"
                            variant="outlined"
                            label={`${group.label}: ${group.actions}`}
                            onDelete={disabled ? undefined : () => setScopes(group.methods.map((m) => scopeOf(group.domain, m)), false)}
                        />
                    ))}
                </SummaryBox>
            )}
        </PickerRoot>
    );
};

export default ApiKeyScopePicker;
